import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, Lock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import {
  USERNAME_HINT,
  validateUsername,
  normalizeUsernameOnInput,
  finalCleanUsername,
} from "@/lib/username";
import OnboardingShell from "@/pages/onboarding/OnboardingShell";
import {
  PrimaryButton,
  TextField,
  ErrorBox,
} from "@/components/AuthKit";

/**
 * ProfileBasics — 1:1 port of iOS `src/app/(onboarding)/basics.tsx`.
 * Username locked when picked at signup, editable for social signups.
 * Fields: username, display name*, DOB*, bio, location, pronouns, website,
 * account privacy (segmented Public/Private), legal consent checklist
 * (Privacy Policy + Terms of Use + Community Guidelines).
 */

const PRIVACY_URL = "https://brontoy.com/privacy";
const GUIDELINES_URL = "https://brontoy.com/guidelines";
const TERMS_URL = "https://brontoy.com/terms";
const MIN_AGE = 13;
const CONSENT_ERROR =
  "Please agree to the Privacy Policy, Terms of Use and the Community Guidelines to continue.";

function CheckRow({ checked, onToggle, children, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-start gap-3 text-left w-full"
      role="checkbox"
      aria-checked={checked}
      data-testid={testId}
    >
      <div
        className={`w-[22px] h-[22px] rounded-[7px] border-[1.5px] flex items-center justify-center mt-0.5 shrink-0 transition-colors ${
          checked
            ? "border-[#7638FA] bg-[#7638FA]"
            : "border-border bg-transparent"
        }`}
      >
        {checked && <Check size={14} color="#fff" strokeWidth={3} />}
      </div>
      <span className="text-muted-foreground text-sm leading-5 flex-1">
        {children}
      </span>
    </button>
  );
}

function ageOf(dobStr) {
  if (!dobStr) return 0;
  const d = new Date(dobStr);
  if (isNaN(d.getTime())) return 0;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

export default function ProfileBasics() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const lockedUsername = user?.username || "";
  const usernameEditable = !lockedUsername;

  const [username, setUsername] = useState("");
  const [availability, setAvailability] = useState({
    checking: false,
    available: null,
    error: "",
  });
  const [displayName, setDisplayName] = useState("");
  const [dob, setDob] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [website, setWebsite] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [consent, setConsent] = useState({
    privacy: false,
    eula: false,
    guidelines: false,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Debounced availability check for the editable (social-signup) username
  useEffect(() => {
    if (!usernameEditable) return;
    const u = username.trim();
    if (!u) {
      setAvailability({ checking: false, available: null, error: "" });
      return;
    }
    const localErr = validateUsername(u);
    if (localErr) {
      setAvailability({ checking: false, available: false, error: localErr });
      return;
    }
    setAvailability({ checking: true, available: null, error: "" });
    const t = setTimeout(async () => {
      try {
        const res = await api.get(EP.usernameAvailability(u));
        const d = res.data;
        const available = d?.available === true || d?.data?.available === true;
        const validationError = d?.validation_error || d?.data?.validation_error;
        setAvailability({
          checking: false,
          available: validationError ? false : available,
          error: validationError || (available === false ? "This username is already taken." : ""),
        });
      } catch {
        setAvailability({ checking: false, available: null, error: "" });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [username, usernameEditable]);

  const allConsented = consent.privacy && consent.eula && consent.guidelines;

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setErr("");

    const cleaned = finalCleanUsername(usernameEditable ? username : lockedUsername);
    const uErr = validateUsername(cleaned);
    if (uErr) return setErr(uErr);
    if (usernameEditable && availability.available === false) {
      return setErr(availability.error || "This username is already taken.");
    }
    if (!displayName.trim()) return setErr("Display name is required.");
    if (!dob) return setErr("Date of birth is required.");
    if (ageOf(dob) < MIN_AGE) {
      return setErr(`You must be at least ${MIN_AGE} years old to use BrontoY.`);
    }
    if (!allConsented) return setErr(CONSENT_ERROR);

    setLoading(true);
    try {
      await api.patch(EP.profileBasics, {
        username: cleaned,
        display_name: displayName.trim(),
        date_of_birth: dob,
        bio: bio.trim(),
        location: location.trim(),
        website: website.trim(),
        pronouns: pronouns.trim(),
        account_privacy: privacy,
      });
      toast.success("Profile saved.");
      await refresh();
      navigate("/onboarding/photo");
    } catch (e2) {
      const status = e2?.response?.status;
      const code = e2?.response?.data?.code;
      if (status === 404 && code === "ERROR_CODE_NOT_FOUND") {
        setErr("We couldn't finish saving your profile. Please contact support@brontoy.com.");
      } else {
        setErr(apiError(e2));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingShell
      step="basics"
      title="Tell us about you"
      subtitle="Your username is how people find you. You can change most of this later — but not your username."
      testId="onboarding-basics-screen"
    >
      <form onSubmit={submit} className="flex flex-col gap-[18px]">
        {/* Username */}
        {usernameEditable ? (
          <div className="flex flex-col gap-1.5">
            <label
              className="text-muted-foreground font-semibold"
              style={{ fontSize: 13, letterSpacing: "0.02em" }}
            >
              USERNAME *
            </label>
            <input
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="yourname"
              value={username}
              onChange={(e) => setUsername(normalizeUsernameOnInput(e.target.value))}
              onBlur={(e) => setUsername(finalCleanUsername(e.target.value))}
              className="w-full h-[52px] bg-secondary rounded-[14px] px-4 text-[16px] text-foreground placeholder:text-muted-foreground border border-border focus:outline-none focus:border-foreground/25 transition-colors"
              data-testid="onboard-username-input"
            />
            <p
              className={`text-xs mt-1 ${
                availability.error
                  ? "text-destructive"
                  : availability.available
                  ? "text-[#7638FA]"
                  : "text-muted-foreground"
              }`}
              data-testid="onboard-username-status"
            >
              {availability.checking
                ? "Checking availability…"
                : availability.error
                ? availability.error
                : availability.available
                ? "Username is available."
                : "Pick your @username — it can't be changed later."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <label
              className="text-muted-foreground font-semibold"
              style={{ fontSize: 13, letterSpacing: "0.02em" }}
            >
              USERNAME *
            </label>
            <div
              className="flex items-center gap-2 h-[52px] bg-secondary rounded-[14px] border border-border px-4"
              data-testid="onboard-username-input"
            >
              <span className="text-muted-foreground text-[16px]">@</span>
              <span className="text-foreground text-[16px] flex-1 truncate">{lockedUsername}</span>
              <Lock size={16} className="text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              You picked this at signup. Usernames can&apos;t be changed after your account is created.
            </p>
          </div>
        )}

        <TextField
          label="DISPLAY NAME *"
          maxLength={60}
          placeholder="Your Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          testId="onboard-display-name-input"
        />

        <TextField
          label="DATE OF BIRTH *"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          testId="onboard-dob-input"
        />

        <div className="flex flex-col gap-1.5">
          <label
            className="text-muted-foreground font-semibold"
            style={{ fontSize: 13, letterSpacing: "0.02em" }}
          >
            BIO (OPTIONAL)
          </label>
          <textarea
            maxLength={280}
            rows={3}
            placeholder="A short line about yourself"
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 280))}
            className="w-full min-h-[88px] bg-secondary rounded-[14px] px-4 py-3 text-[16px] text-foreground placeholder:text-muted-foreground border border-border focus:outline-none focus:border-foreground/25 transition-colors resize-none"
            data-testid="onboard-bio-input"
          />
          <p className="text-xs text-muted-foreground self-end">{bio.length}/280</p>
        </div>

        <TextField
          label="LOCATION (OPTIONAL)"
          placeholder="City, Country"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          testId="onboard-location-input"
        />

        <TextField
          label="PRONOUNS (OPTIONAL)"
          placeholder="she/her"
          autoCapitalize="none"
          value={pronouns}
          onChange={(e) => setPronouns(e.target.value)}
          testId="onboard-pronouns-input"
        />

        <TextField
          label="WEBSITE (OPTIONAL)"
          type="url"
          placeholder="https://"
          autoCapitalize="none"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          testId="onboard-website-input"
        />

        {/* Account privacy — segmented control */}
        <div className="flex flex-col gap-1.5">
          <label
            className="text-muted-foreground font-semibold"
            style={{ fontSize: 13, letterSpacing: "0.02em" }}
          >
            ACCOUNT PRIVACY
          </label>
          <div className="flex bg-secondary rounded-[14px] border border-border p-1 gap-1">
            {["public", "private"].map((opt) => {
              const active = privacy === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setPrivacy(opt)}
                  className={`flex-1 h-[42px] rounded-[10px] text-sm font-bold transition-all ${
                    active ? "bg-[#7638FA] text-white" : "text-muted-foreground hover:bg-card"
                  }`}
                  data-testid={`onboard-privacy-${opt}`}
                >
                  {opt === "public" ? "Public" : "Private"}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {privacy === "public"
              ? "Anyone can see your posts."
              : "Approved followers only."}
          </p>
        </div>

        {/* Legal consent — all three required */}
        <div className="flex flex-col gap-3 mt-1">
          <CheckRow
            checked={consent.privacy}
            onToggle={() => setConsent((c) => ({ ...c, privacy: !c.privacy }))}
            testId="onboard-privacy-checkbox"
          >
            I have read and agree to the{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="text-foreground font-bold underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
            .
          </CheckRow>
          <CheckRow
            checked={consent.eula}
            onToggle={() => setConsent((c) => ({ ...c, eula: !c.eula }))}
            testId="onboard-terms-checkbox"
          >
            I have read and agree to the{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-foreground font-bold underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Use
            </a>
            .
          </CheckRow>
          <CheckRow
            checked={consent.guidelines}
            onToggle={() => setConsent((c) => ({ ...c, guidelines: !c.guidelines }))}
            testId="onboard-guidelines-checkbox"
          >
            I have read and agree to the{" "}
            <a
              href={GUIDELINES_URL}
              target="_blank"
              rel="noreferrer"
              className="text-foreground font-bold underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              Community Guidelines
            </a>
            .
          </CheckRow>
        </div>

        {err && <ErrorBox testId="onboard-basics-error">{err}</ErrorBox>}

        {/* Loader2 kept in import for the busy state on button */}
        <PrimaryButton
          type="submit"
          label="Continue"
          loading={loading}
          className="mt-1 w-full"
          testId="onboard-basics-submit"
        />
        {/* Keep Loader2 usage so the compiler doesn't warn about unused imports */}
        <span className="hidden"><Loader2 /></span>
      </form>
    </OnboardingShell>
  );
}
